import { Col, Flex, Row } from 'antd'
import { TFunction } from 'i18next'

import { useClassifierServiceGetIcd10AmClassifierElementListEndpoint } from 'src/api/queries'
import { Anamnesis, ClassifierCategory, ClassifierType } from 'src/api/requests'
import { DEFAULT_ONE_HUNDRED_PER_PAGE } from 'src/app-constants'
import { Text } from 'src/components'
import { useClassifierId } from 'src/hooks'
import { useLocales } from 'src/locales'

type Props = {
  data: Anamnesis
}

const verifyObjectValues = (obj?: Record<string, unknown> | null) => {
  return Object.values(obj ?? {}).some((item) => !!item)
}

const getFormattedText = (
  obj: Record<string, unknown> | null | undefined,
  translationKey: string,
  t: TFunction
) => {
  return Object.entries(obj ?? {})
    .filter(([_, value]) => !!value && typeof value !== 'object')
    .map(([key]) => t(`${translationKey}.${key}`))
    .join(', ')
}

export const ConsultationFormAnamnesisDetails = ({ data }: Props) => {
  const { t, currentLang } = useLocales()
  const classifierId = useClassifierId(
    ClassifierType.IC_D10AM,
    ClassifierCategory.DIAGNOSTIC
  )

  // Covered diseases are optional - without ids the list endpoint would return everything
  const hasCoveredDiseases = !!data.coveredDiseasesAndIllnessesIds?.length

  const { data: diseases } =
    useClassifierServiceGetIcd10AmClassifierElementListEndpoint(
      {
        id: String(classifierId),
        ids: data.coveredDiseasesAndIllnessesIds,
        pageNumber: 1,
        pageSize: DEFAULT_ONE_HUNDRED_PER_PAGE
      },
      undefined,
      { enabled: hasCoveredDiseases }
    )

  const hasAnamnesisData = verifyObjectValues(data.familyMedicalAnamnesis)
  const hasVaccines = verifyObjectValues(data.vaccines)

  const hasReproductiveAndSexualHealth = verifyObjectValues(
    data.reproductiveAndSexualHealth
  )

  const hasSubstanceConsumption = verifyObjectValues(data.substanceConsumption)

  const hasPregnancy =
    data.pregnancy?.planned ||
    data.pregnancy?.current?.isSelected ||
    data.pregnancy?.previous?.isSelected

  const familyMedicalAnamnesis = getFormattedText(
    data.familyMedicalAnamnesis,
    'consultation_form.family_medical_anamnesis',
    t
  )

  const substanceConsumption = getFormattedText(
    data.substanceConsumption,
    'consultation_form.substance_consumption',
    t
  )

  const reproductiveAndSexualHealth = getFormattedText(
    data.reproductiveAndSexualHealth,
    'consultation_form.reproductive_and_sexual_health',
    t
  )

  return (
    <Row gutter={[24, 24]}>
      <Col span={24}>
        <Text size="xl">{t('consultation_form.anamnesis_section')}</Text>
      </Col>

      {hasCoveredDiseases && (
        <Col span={24}>
          <Flex vertical gap={4}>
            <Text>{t('consultation_form.covered_diseases')}</Text>

            {diseases?.data?.map((item) => (
              <Text key={item.id} weight={400}>
                {item.translation?.[currentLang.value]}
              </Text>
            ))}
          </Flex>
        </Col>
      )}

      {data.surgeriesInAnamnesis && (
        <Col span={24}>
          <Flex vertical gap={4}>
            <Text>{t('consultation_form.surgeries_in_anamnesis')}</Text>

            <Text weight={400}>{data.surgeriesInAnamnesis}</Text>
          </Flex>
        </Col>
      )}

      {data.itsInAnamnesis && (
        <Col span={24}>
          <Text>
            {t('consultation_form.its_in_anamnesis')} -{' '}
            <Text weight={400}>
              {t(`consultation_form.${data.itsInAnamnesis}`)}
            </Text>
          </Text>
        </Col>
      )}

      {hasVaccines && (
        <Col span={24}>
          <Flex vertical gap={4}>
            <Text>{t('consultation_form.vaccines')}</Text>

            <Text weight={400}>
              {data?.vaccines?.nationalCalendar
                ? t('consultation_form.national_calendar')
                : data?.vaccines?.other?.value}
            </Text>
          </Flex>
        </Col>
      )}

      {data.allergies?.medicineAllergies?.isSelected && (
        <Col span={24}>
          <Flex vertical gap={4}>
            <Text>{t('consultation_form.allergies.medicines')}</Text>

            <Text weight={400}>{data.allergies.medicineAllergies.value}</Text>
          </Flex>
        </Col>
      )}

      {data.allergies?.foodAllergies?.isSelected && (
        <Col span={24}>
          <Flex vertical gap={4}>
            <Text>{t('consultation_form.allergies.food')}</Text>

            <Text weight={400}>{data.allergies.foodAllergies.value}</Text>
          </Flex>
        </Col>
      )}

      {data.allergies?.otherAllergies?.isSelected && (
        <Col span={24}>
          <Flex vertical gap={4}>
            <Text>{t('consultation_form.allergies.other')}</Text>

            <Text weight={400}>{data.allergies.otherAllergies.value}</Text>
          </Flex>
        </Col>
      )}

      <Col span={24}>
        <Text>
          {t('consultation_form.body_constitution_changes.title')} -{' '}
          <Text weight={400}>
            {t(
              `consultation_form.body_constitution_changes.${data.bodyConstitutionChanges}`
            )}
          </Text>
        </Text>
      </Col>

      {data.currentMedication && (
        <Col span={24}>
          <Flex vertical gap={4}>
            <Text>{t('consultation_form.current_medication')}</Text>
            <Text weight={400}>{data.currentMedication}</Text>
          </Flex>
        </Col>
      )}

      {hasAnamnesisData && (
        <Col span={24}>
          <Text>
            {t('consultation_form.family_medical_anamnesis.title')} -{' '}
            <Text weight={400}>{familyMedicalAnamnesis}</Text>
          </Text>
        </Col>
      )}

      <Col span={24}>
        <Text>
          {t('consultation_form.tb_patients_contacts')} -{' '}
          <Text weight={400}>
            {t(`epidemiology.symptoms.general.${data.tbPatientsContacts}`)}
          </Text>
        </Text>
      </Col>

      {hasSubstanceConsumption && (
        <Col span={24}>
          <Text>
            {t('consultation_form.substance_consumption.title')} -{' '}
            <Text weight={400}>{substanceConsumption}</Text>
          </Text>
        </Col>
      )}

      {hasReproductiveAndSexualHealth && (
        <Col span={24}>
          <Text>
            {t('consultation_form.reproductive_and_sexual_health.title')} -{' '}
            <Text weight={400}>{reproductiveAndSexualHealth}</Text>
          </Text>
        </Col>
      )}

      {hasPregnancy && (
        <Col span={24}>
          <Flex vertical gap={8}>
            <Text>{t('consultation_form.pregnancy.title')}: </Text>

            {data.pregnancy?.previous?.isSelected && (
              <Text>
                {t('consultation_form.pregnancy.previous_pregnancy')} -{' '}
                <Text weight={400}>{data.pregnancy.previous.value}</Text>
              </Text>
            )}

            {data.pregnancy?.current?.isSelected && (
              <Text>
                {t('consultation_form.pregnancy.current_pregnancy')} -{' '}
                <Text weight={400}>{data.pregnancy.current.value}</Text>
              </Text>
            )}

            {data.pregnancy?.planned && (
              <Text weight={400}>
                {t('consultation_form.pregnancy.planned')}
              </Text>
            )}
          </Flex>
        </Col>
      )}

      <Col span={24}>
        <Flex vertical gap={4}>
          <Text>{t('consultation_form.social_anamnesis.title')}</Text>

          {data.socialAnamnesis?.studies && (
            <Text className="ml-16">
              {t('consultation_form.social_anamnesis.studies.title')} -{' '}
              <Text weight={400}>
                {t(
                  `consultation_form.social_anamnesis.studies.${data.socialAnamnesis.studies}`
                )}
              </Text>
            </Text>
          )}

          {data?.socialAnamnesis?.employmentAndOccupation && (
            <Text className="ml-16">
              {t(
                'consultation_form.social_anamnesis.employment_and_occupation'
              )}{' '}
              -{' '}
              <Text weight={400}>
                {data.socialAnamnesis?.employmentAndOccupation}
              </Text>
            </Text>
          )}
        </Flex>
      </Col>
    </Row>
  )
}
